import {Component, EventEmitter, Inject, Input, OnDestroy, Output} from '@angular/core';
import {AttachmentService, AttachmentServiceToken} from '../attachment.service';
import {AttachmentLinkSharedBase, AttachmentSharedBase, AttachmentUploadStatus, AttachmentUploadUrl} from '../attachments.interface';
import {finalize, mergeMap, Observable, of, Subject, Subscription, takeUntil, tap} from 'rxjs';
import {IterableUtil} from '@app/shared/util/collections/collection-utils';
import {AceLoggerService} from '@app/logging/ace-logger.service';
import {AceLogger} from '@app/logging/ace-logger';

interface AttachmentUploadState {
  uploadSubscriptionByAttachmentId: Map<string, Subscription>;
  fetchUrlsSubscriptions: Set<Subscription>;
  uploadStatusByName: Map<string, AttachmentUploadStatus>;
}

@Component({
  selector: 'app-upload-state',
  templateUrl: './upload-state.component.html',
  styleUrls: ['./upload-state.component.scss'],
  standalone: false
})
export class UploadStateComponent<RecordAttachment extends AttachmentSharedBase<AttachmentLinkSharedBase>, RecordId, UploadRequestBody> implements OnDestroy {
  @Input() recordId: RecordId;
  @Input() autoAssociate: boolean = true;
  @Input() showCancel = false;
  @Input() disabled = false;
  @Output() attachmentUploading = new EventEmitter<boolean>();
  @Output() attachmentAssociated = new EventEmitter<RecordAttachment[]>();
  @Output() hasAttachments = new EventEmitter<boolean>();
  @Output() attachmentOpen = new EventEmitter<string>();
  @Output() associatedAttachmentDelete = new EventEmitter<string>();
  readonly attachmentUploadState: AttachmentUploadState = {
    uploadSubscriptionByAttachmentId: new Map<string, Subscription>(),
    fetchUrlsSubscriptions: new Set<Subscription>(),
    uploadStatusByName: new Map<string, AttachmentUploadStatus>()
  };
  private readonly ngUnsubscribe = new Subject<void>();
  private readonly logger: AceLogger;
  private lastAttachmentUploadingEmit = false;
  private lastHasAttachmentResult = false;

  constructor(@Inject(AttachmentServiceToken) private attachmentService: AttachmentService<RecordAttachment, RecordId, AttachmentUploadUrl, UploadRequestBody>,
              aceLoggerService: AceLoggerService) {
    this.logger = aceLoggerService.create(UploadStateComponent.name);
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  getUploadStatusValues(attachmentUploadState: AttachmentUploadState): AttachmentUploadStatus[] {
    return Array.from(attachmentUploadState.uploadStatusByName.values());
  }

  onAttachmentOpen(uploadStatus: AttachmentUploadStatus) {
    this.attachmentOpen.emit(uploadStatus.uploadUrl.uuid);
  }

  uploadAttachments(attachments: File[], clearUploadStatus?: boolean): void {
    attachments.forEach(file => this.attachmentUploadState.uploadStatusByName.set(file.name, {file, generatingUploadUrl: true}));
    this.emitHasAttachments(this.attachmentUploadState.uploadStatusByName);
    this.emitAttachmentBeingUploaded(this.attachmentUploadState.uploadStatusByName, this.lastAttachmentUploadingEmit);
    const subscription = this.attachmentService.getAttachmentUploadUrls(attachments.map(file => file.name))
      .pipe(
        finalize(() => this.startUploadFilesIfAllUrlsGenerated(this.attachmentUploadState, subscription, clearUploadStatus)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe({
        next: uploadUrls => {
          uploadUrls.forEach(url => {
            const status = this.attachmentUploadState.uploadStatusByName.get(url.file_name);
            if (status) {
              status.uploadUrl = url;
              status.generatingUploadUrl = false;
            }
          });
        },
        error: (error) => {
          this.logger.error(error);
          attachments.forEach(file => this.attachmentUploadState.uploadStatusByName.delete(file.name));
          this.emitAttachmentBeingUploaded(this.attachmentUploadState.uploadStatusByName, this.lastAttachmentUploadingEmit);
        }
      });
    this.attachmentUploadState.fetchUrlsSubscriptions.add(subscription);
  }

  cancelFiles() {
    if (this.attachmentUploadState.uploadSubscriptionByAttachmentId?.size) {
      this.attachmentUploadState.uploadSubscriptionByAttachmentId.forEach(subscription => subscription.unsubscribe());
    }
    if (this.attachmentUploadState.fetchUrlsSubscriptions?.size) {
      this.attachmentUploadState.fetchUrlsSubscriptions.forEach(subscription => subscription.unsubscribe());
    }
    this.resetUploadForm(true);
    this.emitAttachmentBeingUploaded(this.attachmentUploadState.uploadStatusByName, this.lastAttachmentUploadingEmit);
  }

  /** When attachments are not associated yet, we can just remove them here so that they won't be uploaded.
   * When an attachment is associated, we need to confirm and make an API call to delete. So emit it and let parent Component handle it. */
  removeUpload(uploadToRemove: AttachmentUploadStatus) {
    if (uploadToRemove) {
      if (uploadToRemove.associated) {
        this.associatedAttachmentDelete.emit(uploadToRemove.uploadUrl.uuid);
      } else {
        this.attachmentUploadState.uploadStatusByName.delete(uploadToRemove.file.name);
        if (uploadToRemove.uploadUrl?.uuid) {
          const subscriptionToEnd = this.attachmentUploadState.uploadSubscriptionByAttachmentId.get(uploadToRemove.uploadUrl.uuid);
          if (subscriptionToEnd) {
            subscriptionToEnd.unsubscribe();
            this.attachmentUploadState.uploadSubscriptionByAttachmentId.delete(uploadToRemove.uploadUrl.uuid);
          }
        }
        this.emitHasAttachments(this.attachmentUploadState.uploadStatusByName);
        this.emitAttachmentBeingUploaded(this.attachmentUploadState.uploadStatusByName, this.lastAttachmentUploadingEmit);
      }
    }
  }

  removeUploadByAttachmentId(attachmentId: string) {
    const attachmentStatus: AttachmentUploadStatus | null = Array.from(this.attachmentUploadState.uploadStatusByName.values())
      .find((uploadStatus) => uploadStatus?.uploadUrl?.uuid === attachmentId);
    if (attachmentStatus) {
      this.attachmentUploadState.uploadStatusByName.delete(attachmentStatus.uploadUrl.file_name);
      this.emitHasAttachments(this.attachmentUploadState.uploadStatusByName);
    }
  }

  associateUploadedFiles(recordId: RecordId): Observable<RecordAttachment[]> {
    return this.associateUploads(this.attachmentUploadState, recordId, undefined, true);
  }

  resetUploadForm(clearUploadStatus?: boolean): void {
    if (clearUploadStatus) {
      this.attachmentUploadState.uploadStatusByName.clear();
    }
    this.attachmentUploadState.fetchUrlsSubscriptions.clear();
    this.attachmentUploadState.uploadSubscriptionByAttachmentId.clear();
    this.emitHasAttachments(this.attachmentUploadState.uploadStatusByName);
  }

  private emitAttachmentBeingUploaded(uploadStatusByName: Map<string, AttachmentUploadStatus>, lastAttachmentUploadingEmit: boolean | null) {
    const beingUploaded = IterableUtil.first(IterableUtil.filter(uploadStatusByName.values(), status => this.autoAssociate ? !status.associated : !status.complete)) != null;
    if (lastAttachmentUploadingEmit !== beingUploaded) {
      this.lastAttachmentUploadingEmit = beingUploaded;
      this.attachmentUploading.emit(beingUploaded);
    }
  }

  private startUploadFilesIfAllUrlsGenerated(attachmentUploadState: AttachmentUploadState,
                                             subscription: Subscription,
                                             clearUploadStatus?: boolean): void {
    attachmentUploadState.fetchUrlsSubscriptions.delete(subscription);
    if (!attachmentUploadState.fetchUrlsSubscriptions.size && attachmentUploadState.uploadStatusByName.size) {
      this.uploadFiles(attachmentUploadState, clearUploadStatus);
    }
  }

  private uploadFiles(attachmentUploadState: AttachmentUploadState, clearUploadStatus?: boolean): void {
    if (attachmentUploadState.uploadStatusByName?.size) {
      const uploadStatuses: AttachmentUploadStatus[] = [];
      attachmentUploadState.uploadStatusByName.forEach((status: AttachmentUploadStatus) => {
        if (status.progress == null && status.uploadUrl) {
          status.progress = 0;
          const upload$ = this.attachmentService.uploadFile(
            this.attachmentService.getUploadUrl(status.uploadUrl),
            status.file,
            this.uploadProgress.bind(this, attachmentUploadState.uploadStatusByName),
            this.uploadComplete.bind(this, attachmentUploadState.uploadStatusByName));
          const subscription = upload$.pipe(
            finalize(() => {
              attachmentUploadState.uploadSubscriptionByAttachmentId.delete(status.uploadUrl.uuid);
              // If all status in the current batch that haven't been cancelled are done uploading, associate if `autoAssociate` is `true`
              if (this.autoAssociate) {
                const currentBatchUploadStatus = uploadStatuses.filter(uploadStatus => attachmentUploadState.uploadStatusByName.get(uploadStatus.file.name));
                if (currentBatchUploadStatus.every(status => status.complete)) {
                  if (this.recordId != null) {
                    this.associateUploads(attachmentUploadState, this.recordId, uploadStatuses, clearUploadStatus).subscribe();
                  } else {
                    this.logger.error('No `recordId` Input provided for associating of attachments to record.');
                  }
                }
              }
            }),
            takeUntil(this.ngUnsubscribe)
          ).subscribe();
          attachmentUploadState.uploadSubscriptionByAttachmentId.set(status.uploadUrl.uuid, subscription);
          uploadStatuses.push(status);
        }
      });
    }
  }

  private associateUploads(attachmentUploadState: AttachmentUploadState,
                           recordId: RecordId,
                           uploadStatuses?: AttachmentUploadStatus[],
                           clearUploadStatus?: boolean): Observable<RecordAttachment[]> {
    // Need to filter from `attachmentUploadState.uploadStatusByName` to exclude uploads that were cancelled.
    const statusListToAssociate: AttachmentUploadStatus[] = uploadStatuses
      ? uploadStatuses.filter(uploadStatus => attachmentUploadState.uploadStatusByName.get(uploadStatus.file.name))
      : Array.from(attachmentUploadState.uploadStatusByName.values());
    if (statusListToAssociate.length) {
      return of(statusListToAssociate).pipe(
        tap((statusesToAssociate: AttachmentUploadStatus[]) => statusesToAssociate.forEach(status => status.associating = true)),
        mergeMap((statusToAssociate: AttachmentUploadStatus[]) => this.attachmentService.associateAttachments(
          recordId,
          statusToAssociate.map(status => status.uploadUrl)
        )),
        tap((uploadedAttachments: RecordAttachment[]) => {
          statusListToAssociate.forEach(status => {
            status.associated = !!uploadedAttachments.find(attachment => this.attachmentService.getAttachmentId(attachment) === status.uploadUrl.uuid);
            status.associating = !status.associated && status.associating;
          });
          this.attachmentAssociated.emit(uploadedAttachments);
        }),
        finalize(() => this.afterAttachmentAssociated(attachmentUploadState, statusListToAssociate, clearUploadStatus)),
        takeUntil(this.ngUnsubscribe)
      );
    } else {
      return of([]);
    }
  }

  private uploadProgress(uploadStatusByName: Map<string, AttachmentUploadStatus>, filename: string, progress: number): void {
    const status = uploadStatusByName.get(filename);
    if (status) {
      status.progress = progress;
    }
  }

  private uploadComplete(uploadStatusByName: Map<string, AttachmentUploadStatus>, filename: string): void {
    const status = uploadStatusByName.get(filename);
    if (status) {
      status.progress = 100;
      status.complete = true;
    }
    this.emitAttachmentBeingUploaded(uploadStatusByName, this.lastAttachmentUploadingEmit);
  }

  private afterAttachmentAssociated(attachmentUploadState: AttachmentUploadState,
                                    uploadStatuses: AttachmentUploadStatus[],
                                    clearUploadStatus?: boolean): void {
    if (clearUploadStatus) {
      uploadStatuses?.forEach(s => attachmentUploadState.uploadStatusByName.delete(s.file.name));
      this.emitHasAttachments(attachmentUploadState.uploadStatusByName);
    }
    if (!attachmentUploadState.fetchUrlsSubscriptions.size && !attachmentUploadState.uploadSubscriptionByAttachmentId.size) {
      this.resetUploadForm(clearUploadStatus);
    }
    this.emitAttachmentBeingUploaded(attachmentUploadState.uploadStatusByName, this.lastAttachmentUploadingEmit);
  }

  private emitHasAttachments(uploadStatusByName: Map<string, AttachmentUploadStatus>) {
    const hasAttachments = !!uploadStatusByName.size;
    if (this.lastHasAttachmentResult !== hasAttachments) {
      this.lastHasAttachmentResult = hasAttachments;
      this.hasAttachments.emit(this.lastHasAttachmentResult);
    }
  }
}
